//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Wizard validation
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier            Issue# Description
//region Version 1.0.0.0
//    001            Sean Flook                 Initial Revision.
//    002   24.03.23 Sean Flook         WI40608 Changes required to correctly handle changes to PAO details for child properties.
//    003   24.11.23 Sean Flook                 Moved Box to @mui/system.
//    004   30.11.23 Sean Flook                 Changes required to handle Scottish authorities.
//    005   27.03.24 Sean Flook                 Changes required to remove warnings.
//    006   09.04.24 Sean Flook       IMANN-376 Allow lookups to be added on the fly.
//    007   14.06.24 Sean Flook       IMANN-451 Various changes required in order for Scottish authorities to be able to choose to create Gaelic records or not.
//    008   19.06.24 Sean Flook       IMANN-629 Changes to code so that current user is remembered and a 401 error displays the login dialog.
//endregion Version 1.0.0.0
//region Version 1.0.1.0
//    009   24.10.24 Sean Flook      IMANN-1033 Only sort and filter lookups when required.
//endregion Version 1.0.1.0
//region Version 1.0.6.0
//    010   11.07.25 Sean Flook       IMANN-1863 Change postcode to a text field.
//    011   17.07.25 Sean Flook       IMANN-1864 Use the new ADSSelectStreetControl for selecting the required street.
//    012   04.08.25 Sean Flook       IMANN-1922 Added the isPostcode attribute to the postcode control.
//    013   05.08.25 Sean Flook       IMANN-1948 Get the street descriptor record when setting the USRN so it is available when generating the address.
//    014   18.08.25 Sean Flook       IMANN-1992 When getting the street descriptor also set the address preview.
//    015   18.08.25 Sean Flook       IMANN-1992 When getting the street descriptor for a Scottish authority always use ENG for the language.
//endregion Version 1.0.6.0
//region Version 1.0.7.0
//    016   13.06.25 Sean Flook       IMANN-1812 Moved defaults.
//    017   12.02.26 Sean Flook       IMANN-2218 Added some error handling.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useContext, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

import LookupContext from "../context/lookupContext";
import SettingsContext from "../context/settingsContext";
import UserContext from "../context/userContext";

import { Box } from "@mui/material";
import ADSAddressableObjectControl from "../components/ADSAddressableObjectControl";
import ADSPaoDetailsControl from "../components/ADSPaoDetailsControl";
import ADSSelectControl from "../components/ADSSelectControl";
import ADSTextControl from "../components/ADSTextControl";
import ADSReadOnlyControl from "../components/ADSReadOnlyControl";
import AddLookupDialog from "../dialogs/AddLookupDialog";
import ADSSelectStreetControl from "../components/ADSSelectStreetControl";

import { addLookup, getLookupVariantString, stringToSentenceCase } from "../utils/HelperUtils";
import { GetStreetDescriptor, streetDescriptorToTitleCase } from "../utils/StreetUtils";

WizardAddressDetails1Tab.propTypes = {
  data: PropTypes.object,
  isChild: PropTypes.bool,
  language: PropTypes.string.isRequired,
  errors: PropTypes.array,
  onDataChanged: PropTypes.func.isRequired,
  onErrorChanged: PropTypes.func.isRequired,
};

function WizardAddressDetails1Tab({ data, isChild = false, language, errors, onDataChanged, onErrorChanged }) {
  const lookupContext = useContext(LookupContext);
  const settingsContext = useContext(SettingsContext);
  const userContext = useContext(UserContext);

  const [saoStartNumber, setSaoStartNumber] = useState("");
  const [saoStartSuffix, setSaoStartSuffix] = useState("");
  const [saoEndNumber, setSaoEndNumber] = useState("");
  const [saoEndSuffix, setSaoEndSuffix] = useState("");
  const [saoText, setSaoText] = useState("");
  const [paoStartNumber, setPaoStartNumber] = useState("");
  const [paoStartSuffix, setPaoStartSuffix] = useState("");
  const [paoEndNumber, setPaoEndNumber] = useState("");
  const [paoEndSuffix, setPaoEndSuffix] = useState("");
  const [paoText, setPaoText] = useState("");
  const [paoDetails, setPaoDetails] = useState("");
  const [usrn, setUsrn] = useState(0);
  const streetDescriptorRecord = useRef(null);
  const [postTownRef, setPostTownRef] = useState(0);
  const [subLocalityRef, setSubLocalityRef] = useState(0);
  const [postcode, setPostcode] = useState(0);
  const [postTownLookup, setPostTownLookup] = useState([]);
  const [subLocalityLookup, setSubLocalityLookup] = useState([]);

  const [addressPreview, setAddressPreview] = useState("");

  const [saoStartNumError, setSaoStartNumError] = useState(null);
  const [saoStartSuffixError, setSaoStartSuffixError] = useState(null);
  const [saoEndNumError, setSaoEndNumError] = useState(null);
  const [saoEndSuffixError, setSaoEndSuffixError] = useState(null);
  const [saoTextError, setSaoTextError] = useState(null);
  const [paoStartNumError, setPaoStartNumError] = useState(null);
  const [paoStartSuffixError, setPaoStartSuffixError] = useState(null);
  const [paoEndNumError, setPaoEndNumError] = useState(null);
  const [paoEndSuffixError, setPaoEndSuffixError] = useState(null);
  const [paoTextError, setPaoTextError] = useState(null);
  const [paoDetailsError, setPaoDetailsError] = useState(null);
  const [usrnError, setUsrnError] = useState(null);
  const [postTownRefError, setPostTownRefError] = useState(null);
  const [subLocalityRefError, setSubLocalityRefError] = useState(null);
  const [postcodeError, setPostcodeError] = useState(null);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [lookupType, setLookupType] = useState("unknown");
  const [engError, setEngError] = useState(null);
  const [altLanguageError, setAltLanguageError] = useState(null);

  const updatedDataRef = useRef(null);
  const addResult = useRef(null);
  const currentVariant = useRef(null);

  /**
   * Method to get the updated data object after a change.
   *
   * @param {string} field The name of the field that has been updated.
   * @param {number|string|null} newValue The new value for the field.
   * @returns {object} The updated data abject.
   */
  const getUpdatedData = (updatedField, newValue) => {
    const updatedData = {
      language: data.language,
      saoStartNumber:
        updatedField === "saoStartNumber"
          ? newValue
          : updatedDataRef.current
            ? updatedDataRef.current.saoStartNumber
            : saoStartNumber,
      saoStartSuffix:
        updatedField === "saoStartSuffix"
          ? newValue
          : updatedDataRef.current
            ? updatedDataRef.current.saoStartSuffix
            : saoStartSuffix,
      saoEndNumber:
        updatedField === "saoEndNumber"
          ? newValue
          : updatedDataRef.current
            ? updatedDataRef.current.saoEndNumber
            : saoEndNumber,
      saoEndSuffix:
        updatedField === "saoEndSuffix"
          ? newValue
          : updatedDataRef.current
            ? updatedDataRef.current.saoEndSuffix
            : saoEndSuffix,
      saoText:
        updatedField === "saoText" ? newValue : updatedDataRef.current ? updatedDataRef.current.saoText : saoText,
      paoStartNumber:
        updatedField === "paoStartNumber"
          ? newValue
          : updatedDataRef.current
            ? updatedDataRef.current.paoStartNumber
            : paoStartNumber,
      paoStartSuffix:
        updatedField === "paoStartSuffix"
          ? newValue
          : updatedDataRef.current
            ? updatedDataRef.current.paoStartSuffix
            : paoStartSuffix,
      paoEndNumber:
        updatedField === "paoEndNumber"
          ? newValue
          : updatedDataRef.current
            ? updatedDataRef.current.paoEndNumber
            : paoEndNumber,
      paoEndSuffix:
        updatedField === "paoEndSuffix"
          ? newValue
          : updatedDataRef.current
            ? updatedDataRef.current.paoEndSuffix
            : paoEndSuffix,
      paoText:
        updatedField === "paoText" ? newValue : updatedDataRef.current ? updatedDataRef.current.paoText : paoText,
      paoDetails:
        updatedField === "paoDetails"
          ? newValue
          : updatedDataRef.current
            ? updatedDataRef.current.paoDetails
            : paoDetails,
      usrn: updatedField === "usrn" ? newValue : updatedDataRef.current ? updatedDataRef.current.usrn : usrn,
      postcode:
        updatedField === "postcode" ? newValue : updatedDataRef.current ? updatedDataRef.current.postcode : postcode,
      subLocalityRef:
        updatedField === "subLocalityRef"
          ? newValue
          : updatedDataRef.current
            ? updatedDataRef.current.subLocalityRef
            : subLocalityRef,
      postTownRef:
        updatedField === "postTownRef"
          ? newValue
          : updatedDataRef.current
            ? updatedDataRef.current.postTownRef
            : postTownRef,
    };

    updatedDataRef.current = updatedData;

    const postTownRecord = settingsContext.isWelsh
      ? lookupContext.currentLookups.postTowns.find(
          (x) => x.postTownRef === updatedData.postTownRef && x.language === updatedData.language
        )
      : lookupContext.currentLookups.postTowns.find((x) => x.postTownRef === updatedData.postTownRef);
    const subLocalityRecord = settingsContext.isScottish
      ? lookupContext.currentLookups.subLocalities.find((x) => x.subLocalityRef === updatedData.subLocalityRef)
      : null;

    if (isChild) {
      const paoDetails = `${updatedData.paoText ? updatedData.paoText : ""}${
        updatedData.paoText && (updatedData.paoStartNumber || updatedData.paoStartSuffix) ? ", " : ""
      }${updatedData.paoStartNumber ? updatedData.paoStartNumber : ""}${
        updatedData.paoStartSuffix ? updatedData.paoStartSuffix : ""
      }${updatedData.paoEndNumber || updatedData.paoEndSuffix ? "-" : ""}${
        updatedData.paoEndNumber ? updatedData.paoEndNumber : ""
      }${updatedData.paoEndSuffix ? updatedData.paoEndSuffix : ""}`;

      const saoDetails = `${updatedData.saoText ? updatedData.saoText : ""}${
        updatedData.saoText && (updatedData.saoStartNumber || updatedData.saoStartSuffix) ? ", " : ""
      }${updatedData.saoStartNumber ? updatedData.saoStartNumber : ""}${
        updatedData.saoStartSuffix ? updatedData.saoStartSuffix : ""
      }${updatedData.saoEndNumber || updatedData.saoEndSuffix ? "-" : ""}${
        updatedData.saoEndNumber ? updatedData.saoEndNumber : ""
      }${updatedData.saoEndSuffix ? updatedData.saoEndSuffix : ""}`;

      setAddressPreview(
        `${saoDetails.trim()}${saoDetails ? ", " : ""}${paoDetails.trim()}${streetDescriptorRecord.current ? " " : ""}${
          streetDescriptorRecord.current ? streetDescriptorToTitleCase(streetDescriptorRecord.current.displayText) : ""
        }${postTownRecord ? ", " : ""}${postTownRecord ? stringToSentenceCase(postTownRecord.postTown) : ""}${
          subLocalityRecord ? ", " : ""
        }${subLocalityRecord ? stringToSentenceCase(subLocalityRecord.subLocality) : ""}${
          updatedData.postcode ? " " : ""
        }${updatedData.postcode ? updatedData.postcode : ""}`
      );
    } else {
      setAddressPreview(
        `${updatedData.paoText ? updatedData.paoText : ""}${
          updatedData.paoText && (updatedData.paoStartNumber || updatedData.paoStartSuffix) ? ", " : ""
        }${updatedData.paoStartNumber ? updatedData.paoStartNumber : ""}${
          updatedData.paoStartSuffix ? updatedData.paoStartSuffix : ""
        }${updatedData.paoEndNumber || updatedData.paoEndSuffix ? "-" : ""}${
          updatedData.paoEndNumber ? updatedData.paoEndNumber : ""
        }${updatedData.paoEndSuffix ? updatedData.paoEndSuffix : ""}${streetDescriptorRecord.current ? " " : ""}${
          streetDescriptorRecord.current ? streetDescriptorToTitleCase(streetDescriptorRecord.current.displayText) : ""
        }${postTownRecord ? ", " : ""}${postTownRecord ? stringToSentenceCase(postTownRecord.postTown) : ""}${
          subLocalityRecord ? ", " : ""
        }${subLocalityRecord ? stringToSentenceCase(subLocalityRecord.subLocality) : ""}${
          updatedData.postcode ? " " : ""
        }${updatedData.postcode ? updatedData.postcode : ""}`
      );
    }

    return updatedData;
  };

  /**
   * Method to return the list of errors after a field has been updated.
   *
   * @param {string} updatedField The field that was updated.
   * @returns {Array} The updated list of errors.
   */
  const getUpdatedErrors = (updatedField) => {
    if (!errors) return null;

    switch (updatedField) {
      case "saoDetails":
        return errors.filter(
          (x) =>
            !["saostartnumber", "saostartsuffix", "saoendnumber", "saoendsuffix", "saotext"].includes(
              x.field.toLowerCase()
            )
        );

      case "paoDetails":
        return errors.filter(
          (x) =>
            !["paostartnumber", "paostartsuffix", "paoendnumber", "paoendsuffix", "paotext"].includes(
              x.field.toLowerCase()
            )
        );

      default:
        return errors.filter((x) => x.field.toLowerCase() !== updatedField);
    }
  };

  /**
   * Event to handle when the SAO start number is changed.
   *
   * @param {number|null} newValue The new SAO start number.
   */
  const handleSaoStartNumberChangeEvent = (newValue) => {
    setSaoStartNumber(newValue);
    if (onDataChanged && saoStartNumber !== newValue) onDataChanged(getUpdatedData("saoStartNumber", newValue));
    if (onErrorChanged) onErrorChanged(getUpdatedErrors("saoDetails"));
  };

  /**
   * Event to handle when the SAO start suffix is changed.
   *
   * @param {string|null} newValue The new SAO start suffix.
   */
  const handleSaoStartSuffixChangeEvent = (newValue) => {
    setSaoStartSuffix(newValue);
    if (onDataChanged && saoStartSuffix !== newValue) onDataChanged(getUpdatedData("saoStartSuffix", newValue));
    if (onErrorChanged) onErrorChanged(getUpdatedErrors("saoDetails"));
  };

  /**
   * Event to handle when the SAO end number is changed.
   *
   * @param {number|null} newValue The new SAO end number.
   */
  const handleSaoEndNumberChangeEvent = (newValue) => {
    setSaoEndNumber(newValue);
    if (onDataChanged && saoEndNumber !== newValue) onDataChanged(getUpdatedData("saoEndNumber", newValue));
    if (onErrorChanged) onErrorChanged(getUpdatedErrors("saoDetails"));
  };

  /**
   * Event to handle when the SAO end suffix is changed.
   *
   * @param {string|null} newValue The new SAO end suffix.
   */
  const handleSaoEndSuffixChangeEvent = (newValue) => {
    setSaoEndSuffix(newValue);
    if (onDataChanged && saoEndSuffix !== newValue) onDataChanged(getUpdatedData("saoEndSuffix", newValue));
    if (onErrorChanged) onErrorChanged(getUpdatedErrors("saoDetails"));
  };

  /**
   * Event to handle when the SAO text is changed.
   *
   * @param {string|null} newValue The new SAO text.
   */
  const handleSaoTextChangeEvent = (newValue) => {
    setSaoText(newValue);
    if (onDataChanged && saoText !== newValue) onDataChanged(getUpdatedData("saoText", newValue));
    if (onErrorChanged) onErrorChanged(getUpdatedErrors("saoDetails"));
  };

  /**
   * Event to handle when the PAO start number is changed.
   *
   * @param {number|null} newValue The new PAO start number.
   */
  const handlePaoStartNumberChangeEvent = (newValue) => {
    setPaoStartNumber(newValue);
    if (onDataChanged && paoStartNumber !== newValue) onDataChanged(getUpdatedData("paoStartNumber", newValue));
    if (onErrorChanged) onErrorChanged(getUpdatedErrors("paoDetails"));
  };

  /**
   * Event to handle when the PAO start suffix is changed.
   *
   * @param {string|null} newValue The new PAO start suffix.
   */
  const handlePaoStartSuffixChangeEvent = (newValue) => {
    setPaoStartSuffix(newValue);
    if (onDataChanged && paoStartSuffix !== newValue) onDataChanged(getUpdatedData("paoStartSuffix", newValue));
    if (onErrorChanged) onErrorChanged(getUpdatedErrors("paoDetails"));
  };

  /**
   * Event to handle when the PAO end number is changed.
   *
   * @param {number|null} newValue The new PAO end number.
   */
  const handlePaoEndNumberChangeEvent = (newValue) => {
    setPaoEndNumber(newValue);
    if (onDataChanged && paoEndNumber !== newValue) onDataChanged(getUpdatedData("paoEndNumber", newValue));
    if (onErrorChanged) onErrorChanged(getUpdatedErrors("paoDetails"));
  };

  /**
   * Event to handle when the PAO end suffix is changed.
   *
   * @param {string|null} newValue The new PAO end suffix.
   */
  const handlePaoEndSuffixChangeEvent = (newValue) => {
    setPaoEndSuffix(newValue);
    if (onDataChanged && paoEndSuffix !== newValue) onDataChanged(getUpdatedData("paoEndSuffix", newValue));
    if (onErrorChanged) onErrorChanged(getUpdatedErrors("paoDetails"));
  };

  /**
   * Event to handle when the PAO text is changed.
   *
   * @param {string|null} newValue The new PAO text.
   */
  const handlePaoTextChangeEvent = (newValue) => {
    setPaoText(newValue);
    if (onDataChanged && paoText !== newValue) onDataChanged(getUpdatedData("paoText", newValue));
    if (onErrorChanged) onErrorChanged(getUpdatedErrors("paoDetails"));
  };

  /**
   * Event to handle when the USRN is changed.
   *
   * @param {number|null} newValue The new USRN.
   */
  const handleUsrnChangeEvent = (newValue) => {
    setUsrn(newValue);
    if (onDataChanged && usrn !== newValue) onDataChanged(getUpdatedData("usrn", newValue));
    if (onErrorChanged) onErrorChanged(getUpdatedErrors("usrn"));
  };

  /**
   * Event to handle when the post town reference is changed.
   *
   * @param {number|null} newValue The new post town reference.
   */
  const handlePostTownRefChangeEvent = (newValue) => {
    setPostTownRef(newValue);
    if (onDataChanged && postTownRef !== newValue) onDataChanged(getUpdatedData("postTownRef", newValue));
    if (onErrorChanged) onErrorChanged(getUpdatedErrors("postTownRef"));
  };

  /**
   * Event to handle when a new post town is added.
   */
  const handleAddPostTownEvent = () => {
    setLookupType("postTown");
    setShowAddDialog(true);
  };

  /**
   * Event to handle when the sub-locality is changed.
   *
   * @param {number|null} newValue The new sub-locality.
   */
  const handleSubLocalityRefChangeEvent = (newValue) => {
    setSubLocalityRef(newValue);
    if (onDataChanged && subLocalityRef !== newValue) onDataChanged(getUpdatedData("subLocalityRef", newValue));
    if (onErrorChanged) onErrorChanged(getUpdatedErrors("subLocalityRef"));
  };

  /**
   * Event to handle when a new sub-locality is added.
   */
  const handleAddSubLocalityEvent = () => {
    setLookupType("subLocality");
    setShowAddDialog(true);
  };

  /**
   * Event to handle when the postcode is changed.
   *
   * @param {number|null} newValue The new postcode.
   */
  const handlePostcodeChangeEvent = (newValue) => {
    setPostcode(newValue);
    if (onDataChanged && postcode !== newValue) onDataChanged(getUpdatedData("postcode", newValue));
    if (onErrorChanged) onErrorChanged(getUpdatedErrors("postcode"));
  };

  /**
   * Event to handle when the PAO details are changed.
   *
   * @param {object|null} data The new PAO details.
   */
  const handlePaoDetailsChanged = (data) => {
    if (data) {
      const numberStart = data.startNumber && Number(data.startNumber) > 0 ? Number(data.startNumber) : null;
      const numberEnd = data.endNumber && Number(data.endNumber) > 0 ? Number(data.endNumber) : null;
      setPaoStartNumber(numberStart);
      if (onDataChanged && paoStartNumber !== numberStart) onDataChanged(getUpdatedData("paoStartNumber", numberStart));
      setPaoStartSuffix(data.startSuffix);
      if (onDataChanged && paoStartSuffix !== data.startSuffix)
        onDataChanged(getUpdatedData("paoStartSuffix", data.startSuffix));
      setPaoEndNumber(numberEnd);
      if (onDataChanged && paoEndNumber !== numberEnd) onDataChanged(getUpdatedData("paoEndNumber", numberEnd));
      setPaoEndSuffix(data.endSuffix);
      if (onDataChanged && paoEndSuffix !== data.endSuffix)
        onDataChanged(getUpdatedData("paoEndSuffix", data.endSuffix));
      setPaoText(data.text);
      if (onDataChanged && paoText !== data.text) onDataChanged(getUpdatedData("paoText", data.text));
      setPaoDetails(data.details);
      if (onDataChanged && paoDetails !== data.details) onDataChanged(getUpdatedData("paoDetails", data.details));
      if (onErrorChanged) onErrorChanged(getUpdatedErrors("paoDetails"));
    }
  };

  /**
   * Method used to add the new lookup.
   *
   * @param {object} data The data returned from the add lookup dialog.
   */
  const handleDoneAddLookup = async (data) => {
    currentVariant.current = getLookupVariantString(data.variant);

    const addResults = await addLookup(
      data,
      settingsContext.authorityCode,
      userContext,
      settingsContext.isWelsh,
      lookupContext.currentLookups
    );

    if (addResults && addResults.result) {
      if (addResults.updatedLookups && addResults.updatedLookups.length > 0)
        lookupContext.onUpdateLookup(data.variant, addResults.updatedLookups);

      switch (data.variant) {
        case "postTown":
          setPostTownRef(addResults.newLookup.postTownRef);
          break;

        case "subLocality":
          setSubLocalityRef(addResults.newLookup.subLocalityRef);
          break;

        default:
          break;
      }

      addResult.current = true;
    } else addResult.current = false;
    setEngError(addResults ? addResults.engError : null);
    setAltLanguageError(addResults ? addResults.altLanguageError : null);

    setShowAddDialog(!addResult.current);
  };

  /**
   * Event to handle when the add lookup dialog is closed.
   */
  const handleCloseAddLookup = () => {
    setShowAddDialog(false);
  };

  useEffect(() => {
    setPostTownLookup(
      lookupContext.currentLookups.postTowns
        ?.filter((x) => x.language === (settingsContext.isScottish ? "ENG" : language) && !x.historic)
        .sort(function (a, b) {
          return a.postTown.localeCompare(b.postTown, undefined, {
            numeric: true,
            sensitivity: "base",
          });
        })
    );
  }, [lookupContext.currentLookups.postTowns, settingsContext.isScottish, language]);

  useEffect(() => {
    setSubLocalityLookup(
      lookupContext.currentLookups.subLocalities
        ?.filter((x) => x.language === "ENG" && !x.historic)
        .sort(function (a, b) {
          return a.subLocality.localeCompare(b.subLocality, undefined, {
            numeric: true,
            sensitivity: "base",
          });
        })
    );
  }, [lookupContext.currentLookups.subLocalities]);

  useEffect(() => {
    const getStreetDescriptor = async () => {
      if (data && data.usrn && data.language) {
        streetDescriptorRecord.current = await GetStreetDescriptor(
          data.usrn,
          settingsContext.isScottish ? "ENG" : data.language,
          userContext
        );

        const postTownRecord = settingsContext.isWelsh
          ? lookupContext.currentLookups.postTowns.find(
              (x) => x.postTownRef === data.postTownRef && x.language === data.language
            )
          : lookupContext.currentLookups.postTowns.find((x) => x.postTownRef === data.postTownRef);
        const subLocalityRecord = settingsContext.isScottish
          ? lookupContext.currentLookups.subLocalities.find((x) => x.subLocalityRef === data.subLocalityRef)
          : null;

        if (isChild) {
          const paoDetails = `${data.paoText ? data.paoText : ""}${
            data.paoText && (data.paoStartNumber || data.paoStartSuffix) ? ", " : ""
          }${data.paoStartNumber ? data.paoStartNumber : ""}${data.paoStartSuffix ? data.paoStartSuffix : ""}${
            data.paoEndNumber || data.paoEndSuffix ? "-" : ""
          }${data.paoEndNumber ? data.paoEndNumber : ""}${data.paoEndSuffix ? data.paoEndSuffix : ""}`;

          const saoDetails = `${data.saoText ? data.saoText : ""}${
            data.saoText && (data.saoStartNumber || data.saoStartSuffix) ? ", " : ""
          }${data.saoStartNumber ? data.saoStartNumber : ""}${data.saoStartSuffix ? data.saoStartSuffix : ""}${
            data.saoEndNumber || data.saoEndSuffix ? "-" : ""
          }${data.saoEndNumber ? data.saoEndNumber : ""}${data.saoEndSuffix ? data.saoEndSuffix : ""}`;

          setAddressPreview(
            `${saoDetails.trim()}${saoDetails ? ", " : ""}${paoDetails.trim()}${
              streetDescriptorRecord.current ? " " : ""
            }${
              streetDescriptorRecord.current
                ? streetDescriptorToTitleCase(streetDescriptorRecord.current.displayText)
                : ""
            }${postTownRecord ? ", " : ""}${postTownRecord ? stringToSentenceCase(postTownRecord.postTown) : ""}${
              subLocalityRecord ? ", " : ""
            }${subLocalityRecord ? stringToSentenceCase(subLocalityRecord.subLocality) : ""}${
              data.postcode ? " " : ""
            }${data.postcode ? data.postcode : ""}`
          );
        } else {
          setAddressPreview(
            `${data.paoText ? data.paoText : ""}${
              data.paoText && (data.paoStartNumber || data.paoStartSuffix) ? ", " : ""
            }${data.paoStartNumber ? data.paoStartNumber : ""}${data.paoStartSuffix ? data.paoStartSuffix : ""}${
              data.paoEndNumber || data.paoEndSuffix ? "-" : ""
            }${data.paoEndNumber ? data.paoEndNumber : ""}${data.paoEndSuffix ? data.paoEndSuffix : ""}${
              streetDescriptorRecord.current ? " " : ""
            }${
              streetDescriptorRecord.current
                ? streetDescriptorToTitleCase(streetDescriptorRecord.current.displayText)
                : ""
            }${postTownRecord ? ", " : ""}${postTownRecord ? stringToSentenceCase(postTownRecord.postTown) : ""}${
              subLocalityRecord ? ", " : ""
            }${subLocalityRecord ? stringToSentenceCase(subLocalityRecord.subLocality) : ""}${
              data.postcode ? " " : ""
            }${data.postcode ? data.postcode : ""}`
          );
        }
      } else {
        streetDescriptorRecord.current = null;
      }
    };

    if (data) {
      setSaoStartNumber(data.saoStartNumber);
      setSaoStartSuffix(data.saoStartSuffix);
      setSaoEndNumber(data.saoEndNumber);
      setSaoEndSuffix(data.saoEndSuffix);
      setSaoText(data.saoText);
      setPaoStartNumber(data.paoStartNumber);
      setPaoStartSuffix(data.paoStartSuffix);
      setPaoEndNumber(data.paoEndNumber);
      setPaoEndSuffix(data.paoEndSuffix);
      setPaoText(data.paoText);
      setPaoDetails(data.paoDetails);
      setUsrn(data.usrn);
      getStreetDescriptor();
      setPostTownRef(data.postTownRef);
      setSubLocalityRef(settingsContext.isScottish ? data.subLocalityRef : null);
      setPostcode(data.postcode);
    }
  }, [language, data, lookupContext, isChild, settingsContext.isScottish, settingsContext.isWelsh, userContext]);

  useEffect(() => {
    setSaoStartNumError(null);
    setSaoStartSuffixError(null);
    setSaoEndNumError(null);
    setSaoEndSuffixError(null);
    setSaoTextError(null);
    setPaoStartNumError(null);
    setPaoStartSuffixError(null);
    setPaoEndNumError(null);
    setPaoEndSuffixError(null);
    setPaoTextError(null);
    setPaoDetailsError(null);
    setUsrnError(null);
    setPostTownRefError(null);
    setSubLocalityRefError(null);
    setPostcodeError(null);

    if (errors && errors.length > 0) {
      for (const error of errors) {
        switch (error.field.toLowerCase()) {
          case "saostartnumber":
            setSaoStartNumError(error.errors);
            break;

          case "saostartsuffix":
            setSaoStartSuffixError(error.errors);
            break;

          case "saoendnumber":
            setSaoEndNumError(error.errors);
            break;

          case "saoendsuffix":
            setSaoEndSuffixError(error.errors);
            break;

          case "saotext":
            setSaoTextError(error.errors);
            break;

          case "paostartnumber":
            setPaoStartNumError(error.errors);
            break;

          case "paostartsuffix":
            setPaoStartSuffixError(error.errors);
            break;

          case "paoendnumber":
            setPaoEndNumError(error.errors);
            break;

          case "paoendsuffix":
            setPaoEndSuffixError(error.errors);
            break;

          case "paotext":
            setPaoTextError(error.errors);
            break;

          case "paodetails":
            setPaoDetailsError(error.errors);
            break;

          case "usrn":
            setUsrnError(error.errors);
            break;

          case "posttown":
          case "posttownref":
            setPostTownRefError(error.errors);
            break;

          case "sublocality":
          case "sublocalityref":
            setSubLocalityRefError(error.errors);
            break;

          case "postcode":
            setPostcodeError(error.errors);
            break;

          default:
            break;
        }
      }
    }
  }, [errors]);

  return (
    <>
      <Box id="wizard-address-settings-1-tab" sx={{ width: "100%" }}>
        {isChild && (
          <ADSAddressableObjectControl
            variant="SAO"
            isEditable
            isRequired
            helperText="The secondary addressable object."
            startNumberValue={saoStartNumber}
            startSuffixValue={saoStartSuffix}
            endNumberValue={saoEndNumber}
            endSuffixValue={saoEndSuffix}
            textValue={saoText}
            startNumberErrorText={saoStartNumError}
            startSuffixErrorText={saoStartSuffixError}
            endNumberErrorText={saoEndNumError}
            endSuffixErrorText={saoEndSuffixError}
            textErrorText={saoTextError}
            onStartNumberChange={handleSaoStartNumberChangeEvent}
            onStartSuffixChange={handleSaoStartSuffixChangeEvent}
            onEndNumberChange={handleSaoEndNumberChangeEvent}
            onEndSuffixChange={handleSaoEndSuffixChangeEvent}
            onTextChange={handleSaoTextChangeEvent}
          />
        )}
        {isChild && (
          <ADSPaoDetailsControl
            label="PAO details"
            isRequired
            data={{
              startNumber: paoStartNumber,
              startSuffix: paoStartSuffix,
              endNumber: paoEndNumber,
              endSuffix: paoEndSuffix,
              text: paoText,
              details: paoDetails,
            }}
            errorText={paoDetailsError}
            onDetailsChanged={handlePaoDetailsChanged}
          />
        )}
        {!isChild && (
          <ADSAddressableObjectControl
            variant="PAO"
            isEditable
            isRequired
            helperText="The primary addressable object."
            startNumberValue={paoStartNumber}
            startSuffixValue={paoStartSuffix}
            endNumberValue={paoEndNumber}
            endSuffixValue={paoEndSuffix}
            textValue={paoText}
            startNumberErrorText={paoStartNumError}
            startSuffixErrorText={paoStartSuffixError}
            endNumberErrorText={paoEndNumError}
            endSuffixErrorText={paoEndSuffixError}
            textErrorText={paoTextError}
            onStartNumberChange={handlePaoStartNumberChangeEvent}
            onStartSuffixChange={handlePaoStartSuffixChangeEvent}
            onEndNumberChange={handlePaoEndNumberChangeEvent}
            onEndSuffixChange={handlePaoEndSuffixChangeEvent}
            onTextChange={handlePaoTextChangeEvent}
          />
        )}
        <ADSSelectStreetControl
          label="Street"
          isEditable
          isRequired
          value={usrn}
          errorText={usrnError}
          onChange={handleUsrnChangeEvent}
          helperText="Unique Street reference number."
        />
        <ADSSelectControl
          label="Post town"
          isEditable
          useRounded
          allowAddLookup
          lookupData={postTownLookup}
          lookupId="postTownRef"
          lookupLabel="postTown"
          value={postTownRef}
          errorText={postTownRefError}
          onChange={handlePostTownRefChangeEvent}
          onAddLookup={handleAddPostTownEvent}
          helperText="Allocated by the Royal Mail to assist in delivery of mail."
        />
        {settingsContext.isScottish && (
          <ADSSelectControl
            label="Sub-locality"
            isEditable
            useRounded
            allowAddLookup
            lookupData={subLocalityLookup}
            lookupId="subLocalityRef"
            lookupLabel="subLocality"
            value={subLocalityRef}
            errorText={subLocalityRefError}
            onChange={handleSubLocalityRefChangeEvent}
            onAddLookup={handleAddSubLocalityEvent}
            helperText="Third level of geographic area name. e.g. to record an island name or property group."
          />
        )}
        <ADSTextControl
          label="Postcode"
          isEditable
          isPostcode
          forceUppercase
          value={postcode}
          errorText={postcodeError}
          id="wizard_address_details_1_postcode"
          maxLength={8}
          helperText="Allocated by the Royal Mail to assist in delivery of mail."
          onChange={handlePostcodeChangeEvent}
        />
        {addressPreview && (
          <ADSReadOnlyControl label="Address preview" value={addressPreview} noLeftPadding boldLabel />
        )}
      </Box>
      <AddLookupDialog
        isOpen={showAddDialog}
        variant={lookupType}
        errorEng={engError}
        errorAltLanguage={altLanguageError}
        onDone={(data) => handleDoneAddLookup(data)}
        onClose={handleCloseAddLookup}
      />
    </>
  );
}

export default WizardAddressDetails1Tab;
